<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-medium"
    title="IP Pool設定"
  >
    <template v-if="isLoaded" v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <!-- BODY -->
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container>
                <!-- 名前 -->
                <v-row>
                  <v-col v-if="ipPool">
                    <app-label-group
                      label="名前"
                      :value="ipPoolInfo.ipPoolName"
                    />
                  </v-col>
                  <v-col v-else>
                    <app-badge
                      item="名前"
                      msg="半角英数 35文字以内"
                      placement="end"
                    />
                    <validation-provider
                      name="ipPoolName"
                      v-model="ipPoolInfo.ipPoolName"
                      label="名前"
                      :rules="{
                        required: true,
                        max: { length: 35 },
                        is_half_width_alpha_numeric: true,
                        is_not_both: { notBoth: ' ' },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- グローバルIP -->
                <v-row>
                  <v-col>
                    <app-badge item="グローバルIP" :isInfo="false" />
                    <validation-provider
                      name="globalIpAddress"
                      v-model="ipPoolInfo.globalIpAddress"
                      label="グローバルIP"
                      :rules="{
                        required: true,
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-select
                        :items="globalIpList"
                        v-bind="componentField"
                        :error-messages="errorMessage"
                        :field-meta="meta"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>

                <!-- 備考 -->
                <v-row>
                  <v-col>
                    <app-badge
                      item="備考"
                      :msg="
                        $filter.message('info_max_length', { maxLength: '20' })
                      "
                      :isRequired="false"
                      placement="end"
                    />
                    <validation-provider
                      name="description"
                      v-model="ipPoolInfo.description"
                      label="備考"
                      :rules="{
                        max: { length: 20 },
                      }"
                      v-slot="{ componentField, errorMessage, meta }"
                    >
                      <app-form-input
                        v-bind="componentField"
                        :field-meta="meta"
                        :error-messages="errorMessage"
                      />
                    </validation-provider>
                  </v-col>
                </v-row>
              </v-container>
            </div>
          </v-card-text>

          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn type="submit" color="primary" variant="flat">{{
                  ipPool ? "変更" : "追加"
                }}</v-btn>
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import type { PropType } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type { IpPool, IpPoolPost, IpPoolPut } from "@/apis/IpPoolApi";

export default defineComponent({
  name: "IpPoolEdit",
  props: {
    ipPool: {
      type: Object as PropType<IpPool>,
      required: false,
      default: null,
    },
    ipPoolKeyId: {
      type: String as PropType<string>,
      required: true,
    },
    type4Id: {
      type: String as PropType<string>,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      ipPoolInfo: {} as IpPool,
      globalIpList: [] as string[],
      isLoaded: false,
    };
  },
  async mounted() {
    // 更新であればフォームのデータを設定
    if (this.ipPool) this.ipPoolInfo = { ...this.ipPool };
    await this.getGlobalIpList();
    this.isLoaded = true;
  },
  methods: {
    /**
     * グローバルIPリストをAPIから取得する
     */
    async getGlobalIpList() {
      if (!this.type4Id) {
        // Type4IDが無い場合、Type1用の取得APIを呼び出す
        const globalIpList = (
          await this.$api.contract_change_type1.getType1Service()
        ).globalIpAddressList;

        // 取得結果から必要なプロバティのみ抽出して、昇順にソートする
        this.globalIpList = globalIpList
          .map((e) => e.globalIpAddress)
          .sortBy([(e) => e, "asc"]);
      } else {
        // Type4IDがある場合、Type4用の取得APIを呼び出す
        const globalIpList = (
          await this.$api.contract_change_type4.getType4Service(this.type4Id)
        ).globalIpAddressList;

        // 取得結果から必要なプロバティのみ抽出して、昇順にソートする
        this.globalIpList = globalIpList
          .map((e) => e.globalIpAddress)
          .sortBy([(e) => e, "asc"]);
      }
    },
    /**
     * IP Poolの追加・変更を行う
     */
    async submit() {
      // propsから受け取るデータの有無で更新・追加処理をハンドリング
      if (this.ipPool) {
        await this.$confirm("IP Poolを変更します。よろしいですか？");
        await this.modIpPool();
      } else {
        await this.$confirm("IP Poolを追加します。よろしいですか？");
        await this.addIpPool();
      }
      (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
    },
    /** IP Pool追加APIを呼び出す */
    async addIpPool() {
      // 追加データの設定
      const reqData: IpPoolPost = {
        ipPoolName: this.ipPoolInfo.ipPoolName,
        globalIpAddress: this.ipPoolInfo.globalIpAddress,
        description: this.ipPoolInfo.description || null,
        note: null,
        ipPoolKeyId: this.ipPoolKeyId,
      };

      // IP Pool追加APIを呼び出す
      if (!this.type4Id) {
        // Type4IDが無い場合、Type1用の追加APIを呼び出す
        await this.$api.ipPool.postIpPoolType1(reqData);
      } else {
        // Type4IDがある場合、Type4用の追加APIを呼び出す
        await this.$api.ipPool.postIpPoolType4(reqData, this.type4Id);
      }
    },
    /** IP Pool更新APIを呼び出す */
    async modIpPool() {
      // 更新データの設定
      const reqData: IpPoolPut = {
        globalIpAddress: this.ipPoolInfo.globalIpAddress,
        description: this.ipPoolInfo.description || null,
        note: null,
        ipPoolKeyId: this.ipPoolKeyId,
      };

      if (!this.type4Id) {
        // Type4IDが無い場合、Type1用の更新APIを呼び出す
        await this.$api.ipPool.putIpPoolType1(
          reqData,
          this.ipPoolInfo.ipPoolSeq
        );
      } else {
        // Type4IDがある場合、Type4用の更新APIを呼び出す
        await this.$api.ipPool.putIpPoolType4(
          reqData,
          this.ipPoolInfo.ipPoolSeq,
          this.type4Id
        );
      }
    },
  },
});
</script>
